import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close, ContentCopy, OpenInNew, Delete, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { FileMetadata } from "../types/File";
import { getDisplayFileName, getFileCategory } from "../utils/fileDisplay";

interface FilePreviewProps {
  file: FileMetadata | null;
  /** The files the user can page through in the preview (the current list page). */
  files?: FileMetadata[];
  onClose: () => void;
  onNavigate?: (file: FileMetadata) => void;
  onCopyUrl: (url: string) => void;
  onDelete: (file: FileMetadata) => void;
}

export const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  files = [],
  onClose,
  onNavigate,
  onCopyUrl,
  onDelete,
}) => {
  // Retain the last file so the Dialog's close transition can still render its content
  // after the parent clears `file` on close — otherwise the Dialog unmounts instantly and
  // the exit animation never plays.
  const lastFileRef = useRef<FileMetadata | null>(null);
  // Track which file failed to load so a broken/absent asset shows a message instead of a
  // broken-image glyph. Keyed by file key so switching files clears the previous error.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  if (file) lastFileRef.current = file;
  const shown = file ?? lastFileRef.current;

  const open = Boolean(file);
  const index = shown ? files.findIndex((f) => f.key === shown.key) : -1;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < files.length - 1;

  // Arrow keys page through files while the preview is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && hasNext) onNavigate?.(files[index + 1]);
      else if (e.key === "ArrowLeft" && hasPrev) onNavigate?.(files[index - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, index, hasPrev, hasNext, files, onNavigate]);

  if (!shown) return null;

  const name = getDisplayFileName(shown.key, shown.filename);
  const category = getFileCategory(shown.type);
  const isPdf = shown.type === "application/pdf";
  const failed = errorKey === shown.key;
  const markFailed = () => setErrorKey(shown.key);

  const fallback = (message: string) => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  let media: React.ReactNode;
  if (failed) {
    media = fallback(
      "Couldn't load a preview for this file. It may still be processing — try “Open in new tab”."
    );
  } else if (category === "image") {
    media = (
      <img
        src={shown.url}
        alt={name}
        onError={markFailed}
        style={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
      />
    );
  } else if (category === "video") {
    media = (
      <video
        src={shown.url}
        controls
        preload="metadata"
        onError={markFailed}
        style={{ maxWidth: "100%", maxHeight: "70vh" }}
      />
    );
  } else if (category === "audio") {
    media = <audio src={shown.url} controls onError={markFailed} style={{ width: "100%" }} />;
  } else if (isPdf) {
    media = <iframe title={name} src={shown.url} style={{ width: "100%", height: "70vh", border: 0 }} />;
  } else {
    media = fallback(
      "No inline preview available for this file type. Use “Open in new tab” to view it."
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#1e1e24",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}
      >
        <Typography variant="h6" noWrap title={name} sx={{ pr: 2 }}>
          {name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
          {files.length > 1 && (
            <>
              <Tooltip title="Previous">
                <span>
                  <IconButton
                    onClick={() => hasPrev && onNavigate?.(files[index - 1])}
                    disabled={!hasPrev}
                    aria-label="Previous file"
                    size="small"
                  >
                    <ChevronLeft />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5, whiteSpace: "nowrap" }}>
                {index + 1} / {files.length}
              </Typography>
              <Tooltip title="Next">
                <span>
                  <IconButton
                    onClick={() => hasNext && onNavigate?.(files[index + 1])}
                    disabled={!hasNext}
                    aria-label="Next file"
                    size="small"
                  >
                    <ChevronRight />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <IconButton onClick={onClose} aria-label="Close preview" size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 220 }}
      >
        {media}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Box>
          <Tooltip title="Copy URL">
            <IconButton onClick={() => onCopyUrl(shown.url)} color="primary" aria-label="Copy URL">
              <ContentCopy />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <IconButton
              onClick={() => window.open(shown.url, "_blank", "noopener,noreferrer")}
              color="info"
              aria-label="Open in new tab"
            >
              <OpenInNew />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => onDelete(shown)} color="error" aria-label="Delete file">
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
